import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Player from '@vimeo/player';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { X, ArrowRight, Check, Play, Star, MessageCircle, Video, TrendingUp, Volume2, VolumeX, User } from 'lucide-react';
import Footer from '@/components/Footer';
import { trackCta } from '@/lib/trackCta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_golf-coach-admin/artifacts/8hayyo5e_icon_vert_blanc-4k%20.png';
const CHATBOT_MOCKUP = '/chatbot-mockup.jpeg';
const LAURENT_PHOTO = 'https://customer-assets.emergentagent.com/job_golfapp-chatbot/artifacts/pmr4dvuq_WhatsApp%20Image%202025-12-03%20at%2022.45.16.jpeg';

// Default features
const DEFAULT_FEATURES = [
  { id: 'chatbot', title: 'Chatbot Coach IA', description: 'Posez toutes vos questions golf 24h/24', image_url: '/chatbot-mockup.jpeg' },
  { id: 'programs', title: 'Programmes vidéos', description: 'Des dizaines de programmes d\'entraînement', image_url: '' },
  { id: 'analysis', title: 'Analyses de swing', description: 'Envoyez votre swing pour analyse par Laurent', image_url: '' },
  { id: 'progress', title: 'Suivi de progression', description: 'Suivez vos stats et votre évolution', image_url: '' }
];

// Default testimonials
const DEFAULT_TESTIMONIALS = [
  { name: 'Pierre M.', text: 'Le chatbot m\'a aidé à comprendre mes erreurs au putting. J\'ai gagné 3 coups en un mois !', rating: 5 },
  { name: 'Sophie L.', text: 'Les programmes sont très bien structurés. Laurent explique tout clairement.', rating: 5 },
  { name: 'Jean-Claude D.', text: 'Enfin une app qui répond à mes questions à n\'importe quelle heure. Excellent !', rating: 5 }
];

export default function NewHomePage({ user, setUser }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({ first_name: '', last_name: '', email: '', password: '', current_index: '', target_index: '', is_unclassified: false });
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [features, setFeatures] = useState(DEFAULT_FEATURES);
  const [testimonials, setTestimonials] = useState(DEFAULT_TESTIMONIALS);
  const [heroVimeoId, setHeroVimeoId] = useState(null);
  const [heroSoundOn, setHeroSoundOn] = useState(false);
  const [heroVideoReady, setHeroVideoReady] = useState(false);
  const [heroTextHidden, setHeroTextHidden] = useState(false);
  const [hasFreeProgram, setHasFreeProgram] = useState(false);
  const heroIframeRef = useRef(null);
  const heroPlayerRef = useRef(null);

  // Attach the Vimeo Player SDK on mount so we can listen for the actual `playing` event
  // (fires when the first video frame renders, not when the iframe HTML loads).
  // The Laurent-photo fade is driven by `heroVideoReady` = true only after the video actually plays,
  // so there's no dead-air gap between the photo hiding and the video appearing.
  // We deliberately do NOT call player.play() here — the iframe's autoplay=1&muted=1&background=1
  // URL handles native autoplay and calling play() manually breaks it on cold loads.
  useEffect(() => {
    try { heroPlayerRef.current?.destroy?.(); } catch (_) {}
    heroPlayerRef.current = null;
    setHeroVideoReady(false);

    if (!heroVimeoId || !heroIframeRef.current) return;
    let cancelled = false;
    try {
      const p = new Player(heroIframeRef.current);
      heroPlayerRef.current = p;
      p.on('playing', () => { if (!cancelled) setHeroVideoReady(true); });
      p.on('timeupdate', () => { if (!cancelled) setHeroVideoReady(true); });
      p.on('bufferend', () => { if (!cancelled) setHeroVideoReady(true); });
    } catch (_) {
      heroPlayerRef.current = null;
    }
    return () => {
      cancelled = true;
      try { heroPlayerRef.current?.destroy?.(); } catch (_) {}
      heroPlayerRef.current = null;
    };
  }, [heroVimeoId]);

  // Lazily grab the same Player instance for the sound toggle (user-gesture unmute).
  const ensureHeroPlayer = useCallback(() => {
    if (heroPlayerRef.current) return heroPlayerRef.current;
    if (!heroIframeRef.current) return null;
    try {
      const p = new Player(heroIframeRef.current);
      heroPlayerRef.current = p;
      return p;
    } catch (_) {
      heroPlayerRef.current = null;
      return null;
    }
  }, []);

  const toggleHeroSound = useCallback(async () => {
    const next = !heroSoundOn;
    setHeroSoundOn(next);
    const player = ensureHeroPlayer();
    if (!player) return;
    try {
      // Unmute first, then bump volume and ensure playback continues.
      await player.setMuted(!next);
      await player.setVolume(next ? 1 : 0);
      // On iOS, re-issuing play() while still in the user gesture context
      // keeps the audio track active.
      await player.play();
    } catch (e) {
      // Silent fail — user can retry.
    }
  }, [heroSoundOn, ensureHeroPlayer]);

  // Open the auth dialog automatically when a visitor arrives with
  // ?auth=login or ?auth=register (used by the free-preview CTAs).
  useEffect(() => {
    const authParam = searchParams.get('auth');
    if (authParam === 'login' || authParam === 'register') {
      setAuthMode(authParam);
      setShowAuthDialog(true);
      // Clean the URL so refreshes don't re-open the dialog
      searchParams.delete('auth');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [plansRes, homepageRes, heroVideoRes, programsRes] = await Promise.all([
          axios.get(`${API}/subscription-plans`),
          axios.get(`${API}/homepage-config`).catch(() => ({ data: {} })),
          axios.get(`${API}/settings/homepage-hero-video`).catch(() => ({ data: {} })),
          axios.get(`${API}/programs`).catch(() => ({ data: [] })),
        ]);

        if (plansRes.data?.plans?.length > 0) {
          setSubscriptionPlans(plansRes.data.plans);
        }

        if (homepageRes.data?.features?.length > 0) {
          setFeatures(homepageRes.data.features);
        }
        if (homepageRes.data?.testimonials?.length > 0) {
          setTestimonials(homepageRes.data.testimonials);
        }
        if (heroVideoRes.data?.vimeo_id) {
          setHeroVimeoId(heroVideoRes.data.vimeo_id);
        }
        // Free program CTA is shown only if at least one visible program is flagged free
        const freeProgs = Array.isArray(programsRes.data)
          ? programsRes.data.filter((p) => p?.is_free && p?.visibility !== 'hidden')
          : [];
        setHasFreeProgram(freeProgs.length > 0);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleAuth = async (e) => {
    e.preventDefault();
    try {
      const endpoint = authMode === 'login' ? '/auth/login' : '/auth/register';
      const payload = authMode === 'login' 
        ? { email: formData.email, password: formData.password }
        : {
            ...formData,
            current_index: formData.is_unclassified ? null : (formData.current_index ? parseFloat(formData.current_index) : null),
            target_index: formData.is_unclassified ? null : (formData.target_index ? parseFloat(formData.target_index) : null),
          };
      const response = await axios.post(`${API}${endpoint}`, payload);
      localStorage.setItem('session_token', response.data.session_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setUser(response.data.user);
      setShowAuthDialog(false);
      toast.success(authMode === 'login' ? 'Connexion réussie' : 'Compte créé avec succès');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'authentification');
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = `${window.location.origin}/dashboard`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const monthlyPlan = subscriptionPlans.find(p => p.duration === 'monthly');
  const yearlyPlan = subscriptionPlans.find(p => p.duration === 'yearly');

  return (
    <div className="min-h-screen bg-gray-900 safe-bottom">
      {/* Navigation - Dark */}
      <nav className="sticky top-0 left-0 right-0 bg-gray-900/95 backdrop-blur-lg z-50 border-b border-gray-800 safe-top">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-20">
            <div className="flex items-center gap-2 md:gap-3 overflow-visible">
              <img src={LOGO_URL} alt="CabGolf" className="h-8 md:h-10 w-auto object-contain rounded-lg" />
              <img src="https://customer-assets.emergentagent.com/job_golf-coach-admin/artifacts/x9qu4qym_nom_vert-4k%20.png" alt="CabGolf" loading="lazy" className="h-4 md:h-5 w-auto object-contain brightness-0 invert" />
            </div>

            <button
              onClick={() => { setAuthMode('login'); setShowAuthDialog(true); }}
              className="text-white touch-target p-2 hover:bg-white/10 rounded-full transition-colors"
              data-testid="menu-login-btn"
              aria-label="Se connecter"
              title="Se connecter"
            >
              <User size={24} />
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section - Netflix cinematic style */}
      <section className="relative w-full overflow-hidden">
        <div className="relative w-full h-[75vh] md:h-[85vh] min-h-[520px] max-h-[820px] overflow-hidden">
          {/* Base dark background — pure site colour, no green flash. */}
          <div className="absolute inset-0 bg-[#0A0F0C]" />

          {/* Laurent photo — loading poster ONLY. Fades to opacity-0 as soon as the Vimeo SDK
              fires `playing`, so it never sits behind the actual video. Uses object-cover to
              fill the hero on all screens (this is a portrait content, we want it edge-to-edge). */}
          <img
            src={LAURENT_PHOTO}
            alt="Coach Laurent Cabanne"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
              heroVimeoId && heroVideoReady ? 'opacity-0' : 'opacity-100'
            }`}
            onError={(e) => { e.target.style.display = 'none'; }}
          />

          {/* Vimeo background video. Uses the classic "cover" trick (width in vh × height in vw)
              so the iframe always overflows the container and fills edge-to-edge, cropping to
              centre — the same behaviour the user liked before (video looked prominent, no black bars). */}
          {heroVimeoId && (
            <div className={`absolute inset-0 overflow-hidden transition-opacity duration-500 ${heroVideoReady ? 'opacity-100' : 'opacity-0'}`}>
              <iframe
                ref={heroIframeRef}
                src={`https://player.vimeo.com/video/${heroVimeoId}?autoplay=1&muted=1&loop=1&background=1&controls=0&title=0&byline=0&portrait=0&dnt=1&playsinline=1&quality=540p`}
                title="CabGolf - Présentation"
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                style={{ width: '177.77vh', height: '56.25vw', minWidth: '100%', minHeight: '100%' }}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}

          {/* Signature gradients - fade out when text hidden so video shows through cleanly */}
          <div
            className={`absolute inset-0 pointer-events-none transition-opacity duration-500 ${heroTextHidden ? 'opacity-0' : 'opacity-100'}`}
            style={{ background: 'linear-gradient(to right, rgba(10,15,12,0.95) 0%, rgba(10,15,12,0.75) 25%, rgba(10,15,12,0.35) 55%, rgba(10,15,12,0.2) 80%)' }}
          />
          <div
            className={`absolute inset-0 pointer-events-none transition-opacity duration-500 ${heroTextHidden ? 'opacity-0' : 'opacity-100'}`}
            style={{ background: 'linear-gradient(to top, #111827 0%, rgba(17,24,39,0.85) 12%, rgba(17,24,39,0.3) 35%, transparent 65%)' }}
          />

          {/* Tap-to-toggle overlay layer (behind CTAs). Only activated when video is present. */}
          {heroVimeoId && (
            <button
              type="button"
              aria-label={heroTextHidden ? 'Afficher le texte' : 'Masquer le texte pour voir la vidéo'}
              onClick={() => setHeroTextHidden((v) => !v)}
              className="absolute inset-0 z-10 cursor-pointer bg-transparent"
              data-testid="hero-toggle-overlay"
            />
          )}

          {/* Content overlay */}
          <div className={`absolute inset-0 flex items-end md:items-center pb-24 md:pb-16 pointer-events-none transition-opacity duration-500 ${heroTextHidden ? 'opacity-0' : 'opacity-100'}`}>
            <div className="w-full max-w-3xl px-4 md:px-16 lg:px-20 pointer-events-auto">
              <h1 className="text-white text-3xl md:text-5xl lg:text-6xl xl:text-7xl font-black tracking-tight leading-[0.95] mb-4 md:mb-6 drop-shadow-2xl">
                Progressez au golf <br className="hidden md:block" />
                avec Cabgolf !
              </h1>
              <p className="hidden md:block text-white/85 text-base md:text-lg lg:text-xl leading-relaxed mb-6 md:mb-8 max-w-2xl drop-shadow-lg">
                Des programmes d'entraînement conçus par Laurent Cabanne qui s'adaptent à votre jeu grâce à notre chatbot et vos statistiques.
              </p>
              <p className="md:hidden text-white/80 text-sm leading-relaxed mb-5 max-w-md drop-shadow-lg">
                Des programmes d'entraînement de Laurent Cabanne pour progresser à ton rythme.
              </p>

              {/* CTAs — Essayer + Voir programme gratuit (si dispo). Se connecter est dans le menu burger */}
              <div className="relative z-20 flex flex-wrap items-center gap-2 md:gap-3">
                <button
                  onClick={() => { trackCta('homepage', 'essayer'); setAuthMode('register'); setShowAuthDialog(true); }}
                  className="relative z-20 flex-1 md:flex-none min-w-0 inline-flex items-center justify-center gap-1.5 md:gap-2 px-3 md:px-8 py-2.5 md:py-3.5 rounded-md font-bold text-sm md:text-base text-white shadow-2xl hover:scale-[1.03] active:scale-95 transition-transform whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}
                  data-testid="cta-signup"
                >
                  <Play className="w-4 h-4 md:w-5 md:h-5 fill-current flex-shrink-0" />
                  <span className="truncate">Essayer</span>
                </button>
                {hasFreeProgram && (
                  <button
                    onClick={() => { trackCta('homepage', 'free_preview'); navigate('/decouverte'); }}
                    className="relative z-20 flex-1 md:flex-none min-w-0 inline-flex items-center justify-center gap-1.5 md:gap-2 px-3 md:px-6 py-2.5 md:py-3.5 rounded-md font-bold text-sm md:text-base bg-emerald-600/90 hover:bg-emerald-500 text-white transition-colors whitespace-nowrap border border-emerald-400/40"
                    data-testid="cta-free-preview"
                  >
                    <span className="truncate">Voir un programme gratuit</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Show-text hint — visible only when hidden, so user can bring the copy back */}
          {heroVimeoId && heroTextHidden && (
            <div className="absolute top-4 md:top-6 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
              <span className="inline-block px-3 py-1.5 rounded-full text-xs bg-black/50 text-white/90 backdrop-blur-md border border-white/10">
                Touche à nouveau pour afficher le texte
              </span>
            </div>
          )}

          {/* Sound toggle */}
          {heroVimeoId && (
            <button
              onClick={toggleHeroSound}
              data-testid="hero-sound-toggle"
              aria-label={heroSoundOn ? 'Couper le son' : 'Activer le son'}
              className="absolute bottom-4 right-4 md:bottom-8 md:right-8 z-30 w-11 h-11 md:w-12 md:h-12 rounded-full bg-black/60 hover:bg-black/80 border border-white/20 flex items-center justify-center text-white transition-all hover:scale-110 backdrop-blur-md"
            >
              {heroSoundOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
          )}
        </div>
      </section>

      {/* Pricing Section - Right after hero */}
      <section id="pricing" className="py-10 md:py-16 px-4 sm:px-6 lg:px-8 bg-gray-800">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">Nos offres</h2>
            <p className="text-base md:text-lg text-gray-300">Choisissez la formule qui vous convient</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto">
            {/* Single Program Purchase */}
            <div className="bg-gradient-to-br from-gray-700 to-gray-800 rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-xl border border-gray-600">
              <div className="mb-6">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-2">Programme unitaire</h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl md:text-5xl font-bold text-white">9€</span>
                  <span className="text-lg text-gray-300">/programme</span>
                </div>
                <p className="text-sm text-gray-400 mt-1">Accès 1 mois au programme choisi</p>
              </div>
              <ul className="space-y-3 mb-6">
                {['1 programme au choix', 'Accès 30 jours', 'Chatbot coach IA illimité', 'Sans engagement'].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="text-green-400 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm md:text-base text-gray-200">{item}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white py-4 md:py-5 text-base md:text-lg"
                onClick={() => { setAuthMode('register'); setShowAuthDialog(true); }}
              >
                Découvrir
              </Button>
            </div>

            {/* Monthly Subscription */}
            <div className="bg-gradient-to-br from-gray-700 to-gray-800 rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-xl border border-gray-600">
              <div className="mb-6">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                  {monthlyPlan?.name || 'Mensuel Standard'}
                </h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl md:text-5xl font-bold text-white">
                    {monthlyPlan?.price || 25}€
                  </span>
                  <span className="text-lg text-gray-300">/mois</span>
                </div>
              </div>
              <ul className="space-y-3 mb-6">
                {['Chatbot coach IA illimité', 'Tous les programmes vidéos', 'Suivi de progression', '1 analyse de swing offerte', 'Sans engagement'].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="text-green-400 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm md:text-base text-gray-200">{item}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-4 md:py-5 text-base md:text-lg"
                onClick={() => { setAuthMode('register'); setShowAuthDialog(true); }}
              >
                Choisir mensuel
              </Button>
            </div>

            {/* Yearly Subscription */}
            <div className="bg-gradient-to-br from-gray-700 to-gray-800 rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-xl border-2 border-green-500 relative">
              <div className="absolute top-0 right-0 bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-1 text-xs md:text-sm font-semibold rounded-bl-xl rounded-tr-2xl">
                Économisez 20%
              </div>
              <div className="mb-6 mt-4 md:mt-0">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                  {yearlyPlan?.name || 'Annuel'}
                </h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl md:text-5xl font-bold text-white">
                    {yearlyPlan?.price || 200}€
                  </span>
                  <span className="text-lg text-gray-300">/an</span>
                </div>
                <p className="text-sm text-green-400 mt-1">
                  soit {Math.round((yearlyPlan?.price || 200) / 12)}€/mois
                </p>
              </div>
              <ul className="space-y-3 mb-6">
                {['Chatbot coach IA illimité', 'Tous les programmes vidéos', 'Suivi de progression', '4 analyses de swing offertes', '2 mois offerts'].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Check className="text-green-400 flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm md:text-base text-gray-200">{item}</span>
                  </li>
                ))}
              </ul>
              <Button 
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-4 md:py-5 text-base md:text-lg"
                onClick={() => { setAuthMode('register'); setShowAuthDialog(true); }}
              >
                Choisir annuel
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - 4 Cards without title */}
      <section id="features" className="py-10 md:py-16 px-4 sm:px-6 lg:px-8 bg-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {features.map((feature, index) => {
              // Icône par défaut selon l'ID
              const getDefaultIcon = () => {
                if (feature.id === 'chatbot') return <MessageCircle className="text-white" size={32} />;
                if (feature.id === 'programs') return <Play className="text-white" size={32} />;
                if (feature.id === 'analysis') return <Video className="text-white" size={32} />;
                if (feature.id === 'progress') return <TrendingUp className="text-white" size={32} />;
                return <MessageCircle className="text-white" size={32} />;
              };
              
              const getGradient = () => {
                if (index === 0) return 'from-green-500 to-green-600';
                if (index === 1) return 'from-blue-500 to-blue-600';
                if (index === 2) return 'from-purple-500 to-purple-600';
                return 'from-yellow-500 to-yellow-600';
              };
              
              return (
                <div key={feature.id} className="bg-gray-700/50 rounded-2xl p-4 md:p-6 text-center hover:bg-gray-700/70 transition-colors">
                  {/* Image or Icon - Toutes les images ont la même taille carrée */}
                  {feature.image_url ? (
                    <div className="w-24 h-24 md:w-32 md:h-32 mx-auto mb-4 rounded-xl overflow-hidden shadow-lg bg-gray-600">
                      <img 
                        src={feature.image_url}
                        alt={feature.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className={`w-24 h-24 md:w-32 md:h-32 mx-auto mb-4 bg-gradient-to-br ${getGradient()} rounded-2xl flex items-center justify-center shadow-lg`}>
                      {getDefaultIcon()}
                    </div>
                  )}
                  <h3 className="text-base md:text-lg font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-xs md:text-sm text-gray-300">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-10 md:py-16 px-4 sm:px-6 lg:px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
              Ils progressent avec CabGolf
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="bg-gray-800 rounded-2xl p-5 md:p-6 border border-gray-700">
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating || 5)].map((_, i) => (
                    <Star key={i} className="text-yellow-400 fill-yellow-400" size={18} />
                  ))}
                </div>
                <p className="text-gray-300 mb-4 text-sm md:text-base">"{testimonial.text}"</p>
                <p className="font-semibold text-white">{testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Prêt à progresser ?
          </h2>
          <p className="text-lg md:text-xl text-gray-300 mb-8">
            Rejoignez CabGolf et commencez votre transformation golfique dès aujourd'hui
          </p>
          <Button 
            size="lg"
            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-lg px-10 py-6 rounded-full shadow-xl"
            onClick={() => { setAuthMode('register'); setShowAuthDialog(true); }}
          >
            Commencer gratuitement
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      {/* Auth Dialog */}
      <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl">{authMode === 'login' ? 'Connexion' : 'Créer un compte'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <Button 
              onClick={handleGoogleLogin} 
              variant="outline" 
              className="w-full py-6 border-2"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continuer avec Google
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Ou</span>
              </div>
            </div>

            <form onSubmit={handleAuth} className="space-y-4">
              {authMode === 'register' && (
                <div className="grid grid-cols-2 gap-3">
                  <Input 
                    placeholder="Prénom"
                    value={formData.first_name} 
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})} 
                    required
                    className="py-6"
                  />
                  <Input 
                    placeholder="Nom"
                    value={formData.last_name} 
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})} 
                    required
                    className="py-6"
                  />
                </div>
              )}
              <Input 
                type="email"
                placeholder="Email"
                value={formData.email} 
                onChange={(e) => setFormData({...formData, email: e.target.value})} 
                required
                className="py-6"
              />
              <Input 
                type="password"
                placeholder="Mot de passe"
                value={formData.password} 
                onChange={(e) => setFormData({...formData, password: e.target.value})} 
                required
                className="py-6"
              />
              {authMode === 'register' && (
                <>
                  <div className="border-t border-gray-200 pt-3 mt-1">
                    <label className="flex items-center gap-2 cursor-pointer mb-3">
                      <input
                        type="checkbox"
                        checked={formData.is_unclassified}
                        onChange={(e) => setFormData({...formData, is_unclassified: e.target.checked, current_index: '', target_index: ''})}
                        className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">Non classé(e)</span>
                    </label>
                    {!formData.is_unclassified && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Index actuel</label>
                          <Input 
                            type="number"
                            step="0.1"
                            placeholder="ex: 22"
                            value={formData.current_index} 
                            onChange={(e) => setFormData({...formData, current_index: e.target.value})} 
                            className="py-5"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Objectif d'index</label>
                          <Input 
                            type="number"
                            step="0.1"
                            placeholder="ex: 18"
                            value={formData.target_index} 
                            onChange={(e) => setFormData({...formData, target_index: e.target.value})} 
                            className="py-5"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
              <Button type="submit" className="w-full bg-green-600 hover:bg-green-700 py-6">
                {authMode === 'login' ? 'Se connecter' : 'Créer un compte'}
              </Button>
            </form>

            {authMode === 'login' && (
              <div className="text-center text-sm mt-3">
                <button 
                  type="button"
                  onClick={() => {
                    setShowAuthDialog(false);
                    navigate('/forgot-password');
                  }}
                  className="text-gray-600 hover:text-green-600"
                >
                  Mot de passe oublié ?
                </button>
              </div>
            )}

            <div className="text-center text-sm mt-3">
              <button 
                onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
                className="text-green-600 hover:text-green-700 font-medium"
              >
                {authMode === 'login' ? 'Créer un compte' : 'Déjà un compte ? Se connecter'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <Footer />
    </div>
  );
}
